import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Hide header on home page, login, and admin routes
  const isHomePage = location.pathname === '/';
  const isLoginPage = location.pathname === '/login';
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/studies');
  if (isHomePage || isLoginPage || isAdminRoute) {
    return null;
  }

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Only show emergency banner on public survey pages
  const showEmergencyBanner = location.pathname.startsWith('/survey/');

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="glass-container">
        <div className="flex justify-between items-center py-3">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="flex-shrink-0">
                <img
                  src="/cc8.png"
                  alt="Crisci Create Logo"
                  className="h-10 w-auto transition-transform group-hover:scale-105"
                />
              </div>
              <div>
                <h1
                  className="text-xl font-bold leading-tight"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--glass-primary-600)'
                  }}
                >
                  Crisci Create
                </h1>
                <p
                  className="text-sm font-medium leading-tight"
                  style={{
                    fontFamily: 'var(--font-body)',
                    color: 'var(--glass-accent-500)'
                  }}
                >
                  Pharmacy Research Platform
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={isActive(item.href) ? 'glass-nav-link-active glass-nav-link' : 'glass-nav-link'}
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Action Button */}
          <div className="hidden md:flex items-center">
            <Link
              to="/admin"
              className="glass-btn glass-btn-primary glass-btn-sm"
            >
              Staff Sign In
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="glass-btn glass-btn-ghost glass-btn-icon"
              aria-expanded={isMenuOpen}
              aria-label="Toggle menu"
            >
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden glass-animate-fade-in">
            <div className="px-2 pt-2 pb-4 space-y-2 border-t" style={{ borderColor: 'var(--glass-border-primary)' }}>
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block ${isActive(item.href) ? 'glass-nav-link-active glass-nav-link' : 'glass-nav-link'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/admin"
                className="glass-btn glass-btn-primary glass-btn-full"
                onClick={() => setIsMenuOpen(false)}
              >
                Staff Sign In
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Emergency Banner - Only shown on public survey pages */}
      {showEmergencyBanner && (
        <div
          className="py-2"
          style={{
            background: 'var(--status-error-light)',
            borderBottom: '1px solid rgba(220, 38, 38, 0.2)'
          }}
        >
          <div className="glass-container">
            <p className="text-xs text-center" style={{ color: 'var(--status-error)' }}>
              <strong>Emergency:</strong> If you experience severe adverse effects, contact your healthcare provider immediately or call emergency services.
            </p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
